import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Calendar, Clock } from 'lucide-react';

const BlogKneePain = () => {
  return (
    <div className="bg-white">

      {/* HERO */}
      <section className="relative h-[460px] overflow-hidden">
        <img
          src="/images/29.png"
          alt="Knee Pain Physiotherapy"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute inset-0 flex items-end pb-12 px-8 md:px-20">
          <div className="max-w-3xl">
            <span className="inline-block bg-orange-500 text-white text-xs font-bold px-4 py-1.5 rounded-full mb-4">ORTHOPEDIC</span>
            <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight drop-shadow-lg mb-4">
              Knee Pain Without Surgery: How Physiotherapy Restores Function
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
              <span className="flex items-center gap-1.5"><Calendar size={14} />8 Apr 2024</span>
              <span>•</span>
              <span className="flex items-center gap-1.5"><Clock size={14} />6 min read</span>
            </div>
          </div>
        </div>
      </section>

      {/* BREADCRUMB */}
      <div className="bg-gray-100 py-3.5">
        <div className="max-w-7xl mx-auto px-6 text-sm flex items-center gap-2">
          <Link to="/" className="text-gray-500 hover:text-[#39a5d3]">Home</Link>
          <span className="text-gray-400">/</span>
          <Link to="/blog" className="text-gray-500 hover:text-[#39a5d3]">Blogs</Link>
          <span className="text-gray-400">/</span>
          <span className="text-[#39a5d3] font-semibold">Knee Pain Without Surgery</span>
        </div>
      </div>

      {/* MAIN CONTENT + SIDEBAR */}
      <section className="py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

            {/* Article */}
            <div className="lg:col-span-2 text-gray-700 leading-relaxed">

              <p className="text-lg leading-relaxed mb-8 border-l-4 border-orange-500 pl-5 bg-orange-50 py-4 pr-4 rounded-r-lg">
                Knee pain is the second most common cause of chronic pain globally, affecting people of all ages — from young athletes to older adults with arthritis. When knee pain strikes, many people assume surgery is inevitable. The reality is that the vast majority of knee conditions respond extremely well to structured physiotherapy, allowing patients to recover full function, return to sport, and delay or completely avoid surgery. This guide explains how physiotherapy treats the most common causes of knee pain and what to expect from treatment.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b-2 border-orange-500/25">The Most Common Causes of Knee Pain</h2>
              <div className="space-y-3 mb-10">
                {[
                  { t: 'Osteoarthritis', d: 'The most common cause of knee pain in adults over 45. Cartilage — the smooth tissue covering the ends of bones — gradually wears down, causing pain, stiffness, swelling, and reduced range of motion. Contrary to what many patients are told, osteoarthritis is not simply a wear-and-tear condition that inevitably requires joint replacement. Physiotherapy-based exercise is the most effective treatment for knee osteoarthritis, producing pain reduction and functional improvement comparable to surgery in many studies.' },
                  { t: 'Anterior Cruciate Ligament (ACL) Injury', d: 'The ACL is one of four key ligaments stabilising the knee. ACL tears are common in sports involving cutting, jumping, and pivoting — football, basketball, badminton, and skiing. Depending on the severity of the tear and the patient\'s activity goals, treatment may be surgical or non-surgical. Even patients who undergo ACL reconstruction require extensive pre- and post-operative physiotherapy for a successful outcome.' },
                  { t: 'Meniscus Tears', d: 'The menisci are two C-shaped cartilage pads that act as shock absorbers in the knee. Tears can occur from acute injury or degenerate over time. Research has consistently shown that physiotherapy produces outcomes equivalent to surgery for many types of meniscal tears, particularly degenerative tears in older adults. Many people with meniscal tears on MRI have no symptoms at all — imaging findings must always be interpreted in the context of the clinical picture.' },
                  { t: 'Patellofemoral Pain Syndrome', d: 'Also called runner\'s knee, this condition causes pain around or behind the kneecap, typically worsened by stairs, squatting, running, or prolonged sitting. It is caused by abnormal tracking of the patella (kneecap) in its groove, usually related to muscle imbalances in the hip, thigh, and calf. It responds very well to physiotherapy targeting these imbalances.' },
                  { t: 'Patellar Tendinopathy', d: 'The patellar tendon connects the kneecap to the shinbone. Overloading this tendon — particularly in jumping sports — causes tendinopathy, characterised by pain just below the kneecap that worsens with activity. Heavy slow resistance training, guided by a physiotherapist, is the evidence-based treatment and is highly effective.' },
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <p className="font-bold text-gray-900 mb-1.5">{item.t}</p>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.d}</p>
                  </div>
                ))}
              </div>

              {/* Mid article image */}
              <div className="my-10 grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100">
                  <img
                    src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&q=80"
                    alt="Knee rehabilitation exercise"
                    className="w-full h-64 object-cover"
                  />
                  <div className="bg-gray-50 px-4 py-2.5 border-t border-gray-100">
                    <p className="text-xs text-gray-500 italic text-center">Structured strengthening exercise is the cornerstone of effective knee pain rehabilitation.</p>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Why Exercise Is the Key Treatment</h3>
                  <p className="text-sm leading-relaxed mb-3">
                    The knee joint is supported by muscles — primarily the quadriceps, hamstrings, glutes, and calf muscles. When these muscles are weak, the knee itself must absorb forces it was not designed to handle alone. This excess loading drives pain, inflammation, and progressive joint damage.
                  </p>
                  <p className="text-sm leading-relaxed mb-3">
                    Strengthening the muscles around the knee reduces the load on the joint itself, decreases pain, improves stability, and restores the ability to perform daily activities and sport. This is not a temporary fix — patients who complete a structured strengthening programme maintain benefits for years.
                  </p>
                  
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-orange-500/25">The Physiotherapy Treatment Approach</h2>
              <div className="space-y-4 mb-10">
                {[
                  { n: '01', t: 'Assessment and Diagnosis', d: 'A thorough assessment identifies the specific structure causing pain, associated muscle weaknesses and movement deficits, contributing factors such as footwear and activity patterns, and your goals — whether that is walking without pain, returning to sport, or avoiding surgery. This determines the specific treatment programme.' },
                  { n: '02', t: 'Pain Management in the Early Phase', d: 'In the initial phase, manual therapy, taping techniques, and targeted gentle exercise reduce pain and swelling enough to allow progressive loading to begin. Ice or heat application, activity modification, and temporary use of anti-inflammatory medication may be recommended by your doctor alongside physiotherapy.' },
                  { n: '03', t: 'Strengthening Programme', d: 'This is the most important phase. Exercises progressively load the quadriceps, hamstrings, glutes, and hip abductors to restore strength and stability. The programme begins with simple isometric exercises and advances to functional movements including squats, lunges, step-ups, and sport-specific exercises depending on your goals.' },
                  { n: '04', t: 'Movement Retraining', d: 'Many knee problems are perpetuated by faulty movement patterns — poor landing mechanics, excessive knee inward collapse during squatting, or asymmetric loading. Video analysis and movement retraining correct these patterns, addressing the root cause rather than just managing symptoms.' },
                  { n: '05', t: 'Return to Activity', d: 'The final phase involves progressively returning to your full activity level — whether that is running, sport, or simply being able to walk and climb stairs comfortably. Criteria-based progression ensures you are ready for each step rather than returning too early and risking re-injury.' },
                ].map(item => (
                  <div key={item.n} className="flex gap-4 bg-white border border-gray-200 rounded-lg p-5">
                    <div className="bg-orange-500 text-white font-bold text-sm w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0">{item.n}</div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">{item.t}</h3>
                      <p className="text-gray-700 text-sm leading-relaxed">{item.d}</p>
                    </div>
                  </div>
                ))}
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-orange-500/25">When Is Surgery Actually Needed?</h2>
              <p className="mb-5">Physiotherapy is not appropriate for every knee condition. Surgery may be required in the following situations:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10">
                {[
                  'Complete ACL rupture in a young, active patient wishing to return to pivoting sport',
                  'Significant mechanical symptoms — locking, giving way — caused by a displaced meniscal tear',
                  'Severe osteoarthritis with bone-on-bone changes causing intractable pain despite 3+ months of physiotherapy',
                  'Fractures around the knee joint requiring surgical fixation',
                  'Ligament injuries causing gross instability that cannot be managed conservatively',
                  'Osteochondral defects — damage to the cartilage surface — in young patients',
                ].map((s, i) => (
                  <div key={i} className="flex items-start gap-3 bg-white border border-gray-200 rounded-lg px-4 py-3">
                    <span className="w-2 h-2 rounded-full bg-orange-500 mt-1.5 flex-shrink-0" />
                    <p className="text-sm">{s}</p>
                  </div>
                ))}
              </div>

              <div className="bg-orange-50 border border-orange-100 rounded-2xl p-7 mb-10">
                <h2 className="text-xl font-bold text-gray-900 mb-3">When to See an Orthopaedic Specialist or Physiotherapist</h2>
                <ul className="space-y-2">
                  {[
                    'Knee pain that has persisted for more than 4 to 6 weeks',
                    'Swelling that does not resolve with rest and ice within a few days',
                    'Knee that locks, clicks loudly, or gives way unexpectedly',
                    'Pain that is waking you at night or preventing normal daily activities',
                    'You have been told you need a knee replacement and want a second opinion',
                    'Pain following a sports injury, fall, or direct impact to the knee',
                  ].map((p, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 flex-shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-10 flex items-center justify-between">
                <Link to="/blog" className="inline-flex items-center gap-2 text-[#39a5d3] font-semibold text-sm hover:underline">
                  <ChevronLeft size={16} /> Back to All Blogs
                </Link>
               
              </div>
            </div>

            {/* Sidebar */}
            <aside className="space-y-7">
              <div className="bg-white rounded-xl shadow-md border border-gray-100">
                <div className="bg-[#19354a] px-5 py-4 rounded-t-xl">
                  <h3 className="text-white font-bold text-sm">Key Facts</h3>
                </div>
                <div className="p-5 space-y-3">
                  {[
                    { l: 'Global chronic pain cause', v: 'Knee pain #2' },
                    { l: 'Meniscal tears needing surgery', v: 'Less than 50%' },
                    { l: 'OA improvement with exercise', v: 'Clinically proven' },
                    { l: 'Typical rehab duration', v: '8–16 weeks' },
                  ].map((f, i) => (
                    <div key={i} className="flex justify-between border-b border-gray-100 pb-3 last:border-0">
                      <p className="text-xs text-gray-500">{f.l}</p>
                      <p className="text-xs font-bold text-[#39a5d3]">{f.v}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md border border-gray-100">
                <div className="bg-[#19354a] px-5 py-4 rounded-t-xl">
                  <h3 className="text-white font-bold text-sm">Physio vs Surgery</h3>
                </div>
                <div className="p-5 space-y-3">
                  {[
                    { label: 'Recovery time', physio: '8–16 weeks', surgery: '6–12 months' },
                    { label: 'Risk', physio: 'Minimal', surgery: 'Infection, clots' },
                    { label: 'Cost', physio: 'Low', surgery: 'High' },
                    { label: 'Long-term outcome', physio: 'Often equal', surgery: 'Case dependent' },
                  ].map((row, i) => (
                    <div key={i} className="border-b border-gray-100 pb-3 last:border-0">
                      <p className="text-xs font-bold text-gray-700 mb-1">{row.label}</p>
                      <div className="flex justify-between">
                        <span className="text-xs text-green-600 font-medium">Physio: {row.physio}</span>
                        <span className="text-xs text-gray-400">Surgery: {row.surgery}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#19354a] rounded-xl p-6 text-white text-center">
                <h3 className="font-bold mb-2">Book an Orthopaedic Consultation</h3>
                <p className="text-white/80 text-xs mb-4">Find verified orthopaedic specialists on Docmize.</p>
                <Link to="/doctors?specialty=Orthopedic" className="block bg-white text-[#19354a] font-bold py-2.5 rounded-lg text-sm hover:bg-gray-100 transition-colors">
                  Find a Specialist
                </Link>
                <div className="mt-4 pt-3 border-t border-white/20">
                  <p className="text-white/70 text-xs">Call us anytime</p>
                  <a href="tel:+918920845297" className="text-white font-bold text-sm">+91 89208 45297</a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogKneePain;